import React, { useEffect, useState } from "react";
import { Card, Row, Col, Tag, Typography, Divider, Input, Select, Space, Empty, Button, Tooltip } from "antd";
import {
    NumberOutlined,
    BarcodeOutlined,
    ToolOutlined,
    SafetyOutlined,
    InfoCircleOutlined,
    SearchOutlined,
    UserOutlined,
    FileTextOutlined,
    PhoneOutlined,
    ShopOutlined,
    SyncOutlined,
} from "@ant-design/icons";
import {
    BarChartOutlined,
    CheckCircleOutlined,
    ClockCircleOutlined,
    SendOutlined,
    DownloadOutlined,
} from "@ant-design/icons";
import axios from 'axios';
import Swal from 'sweetalert2';
import { BASE_URL } from '../../App';
import { Backdrop, CircularProgress } from '@mui/material';

const { Title, Text } = Typography;
const { Search } = Input;
const { Option } = Select;

export default function ReplacementDemo() {
    const [isLoading, setIsLoading] = useState(false);
    const [branches, setBranches] = useState([]);
    const [dealers, setDealers] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedDealer, setSelectedDealer] = useState(null);
    const [replacementList, setReplacementList] = useState([]);
    const [filteredList, setFilteredList] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [expandedCard, setExpandedCard] = useState(null);
    const [Counts, setCounts] = useState({ TotalCount: 0, GivenToCustomer: 0, PendingToCustomer: 0, GivenToCRI: 0, ReceivedFromCRI: 0 });
    const [ShowCards, setShowCards] = useState(false);

    const login_id = localStorage.getItem("LOGIN_ID");
    const branch_code = localStorage.getItem("BRANCH_CODE");

    useEffect(() => {
        fetchBranchList();
    }, []);

    const fetchBranchList = () => {
        setIsLoading(true);
        setShowCards(false);
        const data = {
            login_id: login_id,
            branch_code: branch_code,
        }

        axios.post(BASE_URL + '/getbranchforreport', data)
            .then(response => {
                setBranches(response.data.Branchlist);
            }).catch(error => {
                Swal.fire({
                    icon: "warning",
                    text: error?.response?.data?.message || "Error fetching data"
                });
            }).finally(() => {
                setIsLoading(false);
            });
    };

    const fetchDealerRetailerList = (branch_code) => {
        setIsLoading(true);
        setShowCards(false);
        const data = {
            login_id: login_id,
            BranchCode: branch_code,
        }

        axios.post(BASE_URL + '/GetReplacementDealerRetailer', data)
            .then(response => {
                setDealers(response.data.Data);
            }).catch(error => {
                Swal.fire({
                    icon: "warning",
                    text: error?.response?.data?.message || "Error fetching data"
                });
            }).finally(() => {
                setIsLoading(false);
            });
    };

    const fetchReplacementList = () => {
        if (!selectedDealer) return;

        setIsLoading(true);
        const data = {
            login_id: login_id,
            SelectUserCode: selectedDealer,
        }

        axios.post(BASE_URL + '/GetReplacementList', data)
            .then(response => {
                // setDealers(response.data.Data);
                setReplacementList(response.data.List || []);
                setFilteredList(response.data.List || []);
                const counts = response.data.Counts[0];
                setCounts({
                    TotalCount: counts.TotalCount ?? 0,
                    GivenToCustomer: counts.GivenToCustomer ?? 0,
                    PendingToCustomer: counts.PendingToCustomer ?? 0,
                    GivenToCRI: counts.GivenToCRI ?? 0,
                    ReceivedFromCRI: counts.ReceivedFromCRI ?? 0,
                });
                setShowCards(true);
            }).catch(error => {
                Swal.fire({
                    icon: "warning",
                    text: error?.response?.data?.message || "Error fetching data"
                });
            }).finally(() => {
                setIsLoading(false);
            });
    };

    const handleSearch = (value) => {
        setSearchTerm(value);
        if (!value) return setFilteredList(replacementList);
        const filtered = replacementList.filter((item) =>
            Object.values(item).join(" ").toLowerCase().includes(value.toLowerCase())
        );
        setFilteredList(filtered);
    };

    const highlightText = (text, searchTerm) => {
        if (!searchTerm) return text;
        const regex = new RegExp(`(${searchTerm})`, "gi");
        return text.split(regex).map((part, i) =>
            part.toLowerCase() === searchTerm.toLowerCase() ? (
                <mark key={i} style={{ backgroundColor: "#ffe58f", padding: 0 }}>
                    {part}
                </mark>
            ) : (
                part
            )
        );
    };

    const handleUpdate = (row, index) => {
        if (!row.serialno || !row.new_serialno) {
            return alert("Please enter both Old and New Serial Numbers!");
        }

        // Update the list locally first
        const updatedList = [...filteredList];
        updatedList[index] = { ...updatedList[index], serialno: row.serialno, new_serialno: row.new_serialno };
        setFilteredList(updatedList);

        setIsLoading(true);

        const data = {
            login_id: login_id,
            SelectUserCode: selectedDealer,
            UpdatedList: updatedList  // 👈 send full list
        };

        axios.post(BASE_URL + '/UpdateReplacementList', data)
            .then(response => {
                Swal.fire({
                    icon: "success",
                    text: response?.data?.message || "✅ Replacement list updated successfully!"
                });

                fetchReplacementList();
            })
            .catch(error => {
                Swal.fire({
                    icon: "error",
                    text: error?.response?.data?.message || "Error updating list"
                });
            })
            .finally(() => {
                setIsLoading(false);
            });
    };


    return (
        <div style={{ padding: "24px", background: "linear-gradient(135deg, #eef2f3 0%, #ffffff 100%)", }} >
            <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={isLoading} >
                <CircularProgress color="inherit" />
            </Backdrop>
            <Card
                style={{
                    borderRadius: "16px",
                    boxShadow: "0 6px 20px rgba(0,0,0,0.08)",
                    padding: "15px",
                    background: "linear-gradient(135deg, #ffffff 0%, #f0f5ff 100%)",
                    marginBottom: "24px",
                }}
            >
                <Title level={3} style={{ marginBottom: 20, color: "#003a8c" }}>
                    🔧 Replacement Management
                </Title>

                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} md={8}>
                        <Select
                            showSearch
                            placeholder="Select Branch"
                            style={{ width: "100%" }}
                            onChange={(val) => {
                                setSelectedBranch(val);
                                setSelectedDealer(null);
                                setReplacementList([]);
                                fetchDealerRetailerList(val);
                            }}
                            value={selectedBranch}
                            optionFilterProp="label"
                        >
                            {branches.map((b) => (
                                <Option key={b.branch} value={b.branch} label={b.branch_name}>
                                    {b.branch_name}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={8}>
                        <Select
                            showSearch
                            placeholder="Select Dealer / Retailer"
                            style={{ width: "100%" }}
                            onChange={(val) => setSelectedDealer(val)}
                            value={selectedDealer}
                            disabled={!dealers.length}
                            optionFilterProp="label"
                        >
                            {dealers.map((b) => (
                                <Option key={b.Value} value={b.Value} label={b.Label}>
                                    <ShopOutlined /> {b.Label}
                                </Option>
                            ))}
                        </Select>
                    </Col>

                    <Col xs={24} sm={12} md={8}>
                        <Button
                            type="primary"
                            onClick={fetchReplacementList}
                            disabled={!selectedBranch || !selectedDealer}
                            block
                        >
                            <SyncOutlined spin={isLoading} /> Load Replacement List
                        </Button>
                    </Col>
                </Row>

                {ShowCards && (
                    <>
                        <hr style={{ margin: '8px 0' }} />
                        <Row
                            gutter={[16, 16]}
                            justify="space-around"
                            style={{ marginBottom: "20px", marginTop: "10px" }}
                        >
                            {[
                                {
                                    title: "Total Replacements",
                                    icon: <BarChartOutlined />,
                                    value: Counts.TotalCount,
                                    color: "#003a8c",
                                    bg: "linear-gradient(145deg, #e6f7ff 0%, #f0f5ff 100%)",
                                },
                                {
                                    title: "Given To Customer",
                                    icon: <CheckCircleOutlined />,
                                    value: Counts.GivenToCustomer,
                                    color: "#52c41a",
                                    bg: "linear-gradient(145deg, #f6ffed 0%, #ffffff 100%)",
                                },
                                {
                                    title: "Pending To Customer",
                                    icon: <ClockCircleOutlined />,
                                    value: Counts.PendingToCustomer,
                                    color: "#fa8c16",
                                    bg: "linear-gradient(145deg, #fff7e6 0%, #ffffff 100%)",
                                },
                                {
                                    title: "Given To CRI",
                                    icon: <SendOutlined />,
                                    value: Counts.GivenToCRI,
                                    color: "#1890ff",
                                    bg: "linear-gradient(145deg, #e6f7ff 0%, #ffffff 100%)",
                                },
                                {
                                    title: "Received From CRI",
                                    icon: <DownloadOutlined />,
                                    value: Counts.ReceivedFromCRI,
                                    color: "#389e0d",
                                    bg: "linear-gradient(145deg, #f6ffed 0%, #ffffff 100%)",
                                },
                            ].map((card, index) => (
                                <Col xs={12} sm={8} md={6} lg={4} key={index}>
                                    <Card
                                        bordered={false}
                                        hoverable
                                        style={{
                                            borderRadius: "10px",
                                            background: card.bg,
                                            textAlign: "center",
                                            boxShadow:
                                                "0 2px 6px rgba(0, 0, 0, 0.25), 0 6px 12px rgba(0, 0, 0, 0.25)",
                                            transition: "all 0.25s ease",
                                            height: "110px",
                                            cursor: "pointer",
                                        }}
                                        bodyStyle={{ padding: "10px 8px" }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.transform = "translateY(-4px)";
                                            e.currentTarget.style.boxShadow =
                                                "0 6px 18px rgba(0,0,0,0.50), 0 10px 20px rgba(0,0,0,0.50)";
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.transform = "translateY(0px)";
                                            e.currentTarget.style.boxShadow =
                                                "0 2px 6px rgba(0, 0, 0, 0.20), 0 6px 12px rgba(0, 0, 0, 0.20)";
                                        }}
                                    >
                                        <div
                                            style={{
                                                fontSize: "1.4rem",
                                                color: card.color,
                                                marginBottom: 4,
                                                opacity: 0.85,
                                            }}
                                        >
                                            {card.icon}
                                        </div>
                                        <Title
                                            level={5}
                                            style={{
                                                color: "#333",
                                                fontSize: "13px",
                                                marginBottom: 4,
                                                fontWeight: 600,
                                            }}
                                        >
                                            {card.title}
                                        </Title>
                                        <div
                                            style={{
                                                fontSize: "1.3rem",
                                                fontWeight: 700,
                                                color: card.color,
                                                letterSpacing: "0.3px",
                                            }}
                                        >
                                            {card.value}
                                        </div>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </>
                )}
            </Card>

            {filteredList.length > 0 ? (
                <>

                    {replacementList.length > 0 && (
                        <>
                            <hr style={{ margin: '8px 0' }} />
                            <Search
                                placeholder="Search..."
                                enterButton={<SearchOutlined />}
                                allowClear
                                size="middle" // ✅ smaller size
                                onSearch={handleSearch}
                                onChange={(e) => handleSearch(e.target.value)}
                                style={{
                                    width: "100%", // ✅ smaller width
                                    minWidth: 180,
                                    marginTop: "8px",
                                }}
                            />
                            <hr style={{ margin: '10px 0' }} />
                        </>
                    )}
                    <Row gutter={[16, 16]}>
                        {filteredList.map((row, index) => {
                            const isExpanded = expandedCard === index;
                            return (
                                <Col xs={24} sm={12} lg={8} key={index}>
                                    <Card
                                        hoverable
                                        onClick={() => setExpandedCard(isExpanded ? null : index)}
                                        style={{
                                            borderRadius: 14,
                                            background: isExpanded
                                                ? "linear-gradient(145deg, #e6f7ff 0%, #ffffff 100%)"
                                                : "#fff",
                                            boxShadow: isExpanded
                                                ? "0 2px 6px rgba(0, 0, 0, 0.25), 0 6px 12px rgba(0, 0, 0, 0.25)"
                                                : "0 2px 6px rgba(0, 0, 0, 0.50), 0 6px 12px rgba(0, 0, 0, 0.50)",
                                            transition: "all 0.3s ease",
                                            overflow: "hidden",
                                        }}
                                    >
                                        <Row gutter={[8, 8]}>
                                            <Col xs={24} sm={12}>
                                                <Tag color="blue">
                                                    <NumberOutlined /> Token: {highlightText(row.tokenno, searchTerm)}
                                                </Tag>
                                            </Col>
                                            <Col xs={24} sm={12}>
                                                <Tag color="geekblue">
                                                    <BarcodeOutlined /> Serial: {highlightText(row.serialno, searchTerm)}
                                                </Tag>
                                            </Col>
                                            <Col xs={24} sm={12}>
                                                <Tag color="purple">
                                                    <SafetyOutlined /> Warranty: {row.warranty}
                                                </Tag>
                                            </Col>
                                            <Col xs={24} sm={12}>
                                                <Tag color="purple">
                                                    <FileTextOutlined /> Ref No: {row.referrencenumber}
                                                </Tag>
                                            </Col>
                                            <Col xs={24} sm={12}>
                                                <Tag color="magenta">
                                                    <UserOutlined /> {row.cust_name}
                                                </Tag>
                                            </Col>
                                            <Col xs={24} sm={12}>
                                                <Tag color="magenta">
                                                    <PhoneOutlined /> {row.a_mobileno}
                                                </Tag>
                                            </Col>
                                        </Row>
                                        <hr style={{ margin: '8px 0' }} />
                                        <Text strong>
                                            <ToolOutlined /> Complaints:{" "}
                                        </Text>
                                        <b style={{ color: "#cf1322", margin: 0 }}>
                                            {row.complaints || "No complaints"}
                                        </b>

                                        {isExpanded && (
                                            <div>
                                                <hr style={{ margin: '8px 0' }} />

                                                <Row gutter={[8, 8]}>
                                                    <Col span={24}>
                                                        <Input
                                                            prefix={<BarcodeOutlined />}
                                                            placeholder="Old Serial No"
                                                            value={row.serialno}
                                                            onClick={(e) => e.stopPropagation()} // ✅ Prevent collapse on click
                                                            onChange={(e) => {
                                                                const newList = [...filteredList];
                                                                newList[index].serialno = e.target.value;
                                                                setFilteredList(newList);
                                                            }}
                                                        />
                                                    </Col>
                                                    <Col span={24}>
                                                        <Input
                                                            prefix={<BarcodeOutlined />}
                                                            placeholder="New Serial No"
                                                            value={row.new_serialno}
                                                            onClick={(e) => e.stopPropagation()}
                                                            onChange={(e) => {
                                                                const newList = [...filteredList];
                                                                newList[index].new_serialno = e.target.value;
                                                                setFilteredList(newList);
                                                            }}
                                                        />
                                                    </Col>
                                                    <Col span={24} style={{ textAlign: "center", marginTop: "10px" }}>
                                                        <Tooltip title="Click to update serial numbers">
                                                            <Button
                                                                type="primary"
                                                                shape="round"
                                                                icon={<SyncOutlined />}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleUpdate(row, index);
                                                                }}
                                                            >
                                                                Send & Update
                                                            </Button>
                                                        </Tooltip>
                                                    </Col>
                                                </Row>
                                            </div>
                                        )}
                                    </Card>
                                </Col>
                            );
                        })}
                    </Row>
                </>
            ) : (
                !isLoading && <Empty description="No Replacement Data Found" style={{ marginTop: 50 }} />
            )
            }
            <br /><br /><br />
        </div >
    );
}
